import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Search } from "lucide-react";
import { API, useAuth } from "@/App";
import ClientAudienceStep from "./ClientAudienceStep";
import { SL, WipNote, initials } from "./ClientDialerUi";

const STEPS = ["Basics", "Client list", "Script", "Agents", "Review"];
const ROLE_LABEL = { super_admin: "Super admin", crm_admin: "CRM admin", sales_manager: "Sales manager", sales_agent: "Sales agent", call_agent: "Call agent" };
const SOON = "Coming soon for client campaigns";

function StepDots({ step }) {
  return (
    <div data-testid="cd-step-dots" style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
      {STEPS.map((s, i) => (
        <div key={s} style={{ display: "flex", alignItems: "center" }} title={s}>
          {i > 0 && <div style={{ width: 24, height: 2, background: step > i ? "#1D9E75" : "#eee" }} />}
          <div style={{ width: 22, height: 22, borderRadius: "50%", background: step > i + 1 ? "#1D9E75" : step === i + 1 ? "#2563eb" : "#f5f5f5", color: step >= i + 1 ? "#fff" : "#888", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 600 }}>
            {step > i + 1 ? <Check style={{ width: 10, height: 10 }} /> : i + 1}
          </div>
        </div>
      ))}
    </div>
  );
}

function Basics({ name, setName }) {
  return (<>
    <div style={{ marginBottom: 12 }}>
      <label style={{ ...SL, display: "block", marginBottom: 4 }}>Campaign name</label>
      <input className="input" data-testid="cd-campaign-name" placeholder="e.g. Q3 Client Check-ins" value={name} onChange={(e) => setName(e.target.value)} />
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
      <div>
        <label style={{ ...SL, display: "block", marginBottom: 4 }}>Type</label>
        <select className="input" data-testid="cd-campaign-type" disabled defaultValue="outbound"><option value="outbound">Outbound</option></select>
        <WipNote testid="cd-type-wip">{SOON}</WipNote>
      </div>
      <div>
        <label style={{ ...SL, display: "block", marginBottom: 4 }}>Priority</label>
        <select className="input" data-testid="cd-campaign-priority" disabled defaultValue="normal"><option value="normal">Normal</option></select>
        <WipNote testid="cd-priority-wip">{SOON}</WipNote>
      </div>
    </div>
  </>);
}

function ScriptStep() {
  return (<>
    <div style={{ ...SL, marginBottom: 8 }}>Assign call script</div>
    <div style={{ fontSize: 11, color: "#888", textAlign: "center", padding: "16px 0", border: "1px dashed var(--border)", borderRadius: 8 }}>No client scripts in the library yet.</div>
    <WipNote testid="cd-script-wip">Call scripts for client campaigns are coming soon.</WipNote>
  </>);
}

function AgentsStep({ reps, picked, setPicked }) {
  const [q, setQ] = useState("");
  const list = reps.filter((r) => picked.includes(r.id) || (r.name || "").toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div data-testid="cd-step4" style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Assign agents to this campaign</div>
      <div style={{ fontSize: 12, color: "#888", marginBottom: 10 }}>Select which agents will call this campaign's clients.</div>
      <div style={{ position: "relative", marginBottom: 8 }}>
        <Search style={{ position: "absolute", left: 10, top: 9, width: 13, height: 13, color: "#94a3b8" }} />
        <input data-testid="cd-rep-search" className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search agents…" style={{ paddingLeft: 30, fontSize: 12 }} />
      </div>
      <div data-testid="cd-reps" style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 260, overflowY: "auto", marginBottom: 12 }}>
        {list.length === 0 && <div style={{ fontSize: 11, color: "#888", textAlign: "center", padding: "16px 0", border: "1px dashed var(--border)", borderRadius: 8 }}>No agents match.</div>}
        {list.map((a) => {
          const on = picked.includes(a.id);
          return (
            <label key={a.id} data-testid={`cd-rep-${a.id}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", border: "0.5px solid", borderColor: on ? "#2563eb" : "#eee", borderLeft: on ? "3px solid #2563eb" : "3px solid transparent", borderRadius: 8, background: on ? "#f0f7ff" : "#fff", cursor: "pointer" }}>
              <input type="checkbox" checked={on} onChange={() => setPicked(on ? picked.filter((x) => x !== a.id) : [...picked, a.id])} style={{ width: 13, height: 13 }} data-testid={`cd-rep-check-${a.id}`} />
              <span style={{ width: 28, height: 28, borderRadius: "50%", background: "#E6F1FB", color: "#185FA5", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>{initials(a.name)}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{a.name}</div>
                <div style={{ fontSize: 11, color: "#888" }}>{ROLE_LABEL[a.role] || a.role}</div>
              </div>
            </label>
          );
        })}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 10 }}>
        <div>
          <label style={{ ...SL, display: "block", marginBottom: 4 }}>Distribution method</label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#94a3b8" }}><input type="radio" checked readOnly disabled /> Shared pool — next available client</label>
          <WipNote testid="cd-distribution-wip">{SOON}</WipNote>
        </div>
        <div>
          <label style={{ ...SL, display: "block", marginBottom: 4 }}>Daily target per agent</label>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}><input type="number" className="input" disabled placeholder="—" style={{ width: 100 }} /><span style={{ fontSize: 11, color: "#888" }}>calls / day</span></div>
          <WipNote testid="cd-target-wip">{SOON}</WipNote>
        </div>
      </div>
    </div>
  );
}

function chipsOf(f, owners) {
  const out = [];
  [["states", "state"], ["statuses", "status"], ["services", "service"], ["risks", "risk"]].forEach(([k, l]) => (f[k] || []).forEach((v) => out.push(`${l}: ${v}`)));
  (f.owner_ids || []).forEach((id) => out.push(`owner: ${owners.find((o) => o[0] === id)?.[1] || id}`));
  [["open_ar", "open AR"], ["tickets", "tickets"], ["providers", "providers"]].forEach(([k, l]) => {
    if (f[`${k}_min`] !== undefined && f[`${k}_min`] !== "") out.push(`${l} ≥ ${f[`${k}_min`]}`);
    if (f[`${k}_max`] !== undefined && f[`${k}_max`] !== "") out.push(`${l} ≤ ${f[`${k}_max`]}`);
  });
  if (f.search) out.push(`practice/#: ${f.search}`);
  if ((f.exclude_ids || []).length) out.push(`${f.exclude_ids.length} excluded`);
  return out;
}

function Review({ name, count, f, owners, picked, editing }) {
  const row = { marginBottom: 10, paddingBottom: 10, borderBottom: "0.5px solid #f5f5f5" };
  const lbl = { fontSize: 10, color: "#888", textTransform: "uppercase", marginBottom: 4 };
  return (
    <div data-testid="cd-step5" style={{ background: "#fff", border: "0.5px solid #eee", borderRadius: 10, padding: 16, marginBottom: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Review {editing ? "changes" : "& launch"}</div>
      <div style={row}><div style={lbl}>Campaign</div><div style={{ fontSize: 13 }} data-testid="cd-review-name">{name || <span style={{ color: "#888" }}>Unnamed</span>} · <span style={{ padding: "2px 8px", borderRadius: 10, background: "#EEEDFE", color: "#3C3489", fontSize: 10 }}>outbound</span></div></div>
      <div style={row}>
        <div style={lbl}>Clients</div>
        <div style={{ fontSize: 13 }} data-testid="cd-review-count">{count ?? "—"} {count === 1 ? "client matches" : "clients match"} your filters</div>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
          {chipsOf(f, owners).map((c) => <span key={c} style={{ padding: "1px 6px", borderRadius: 10, fontSize: 10, background: "#DBEAFE", color: "#1D4ED8" }}>{c}</span>)}
        </div>
      </div>
      <div style={row}><div style={lbl}>Script</div><div style={{ fontSize: 13, color: "#888" }}>None — coming soon</div></div>
      <div><div style={lbl}>Team setup</div><div style={{ fontSize: 13 }} data-testid="cd-review-team">{picked.length} agent{picked.length === 1 ? "" : "s"} · Shared pool</div></div>
    </div>
  );
}

/** New/Edit client campaign — same 5-step pop-up as the Leads campaign builder. */
export default function ClientCampaignWizard({ opts, editing, onClose, onSaved }) {
  const { token } = useAuth();
  const [step, setStep] = useState(1);
  const [name, setName] = useState(editing?.name || "");
  const [prompt, setPrompt] = useState(editing?.prompt || "");
  const [f, setF] = useState(editing?.filters || {});
  const [sort, setSort] = useState(editing?.sort || "open_ar_desc");
  const [picked, setPicked] = useState(editing?.assigned_rep_ids || []);
  const [count, setCount] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const blocked = (step === 1 && !name.trim()) || (step === 2 && !count) || (step === 4 && picked.length === 0);
  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/client-dialer/campaigns`, { id: editing?.id, name, prompt, filters: f, sort, assigned_rep_ids: picked }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(editing ? "Campaign updated" : "Campaign launched"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save the campaign"); }
    setSaving(false);
  };
  return (
    <div data-testid="cd-wizard-backdrop" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div data-testid="cd-builder" style={{ background: "#fff", borderRadius: 12, width: step === 2 ? "min(1240px, 96vw)" : "min(760px, 96vw)", maxHeight: "92vh", height: "min(880px, 92vh)", display: "flex", flexDirection: "column", border: "0.5px solid #eee", transition: "width 220ms ease", overflow: "hidden" }}>
        <div style={{ padding: "16px 20px", borderBottom: "0.5px solid #eee", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <span style={{ fontSize: 15, fontWeight: 500 }} data-testid="cd-wizard-title">{editing ? "Edit Campaign" : "New Campaign"} — Step {step} of 5</span>
          <button data-testid="cd-builder-cancel" onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#888" }}>&times;</button>
        </div>
        <div style={{ padding: 20, flex: 1, minHeight: 0, overflowY: "auto" }}>
          <StepDots step={step} />
          {step === 1 && <Basics name={name} setName={setName} />}
          {step === 2 && <ClientAudienceStep f={f} setF={setF} sort={sort} setSort={setSort} prompt={prompt} setPrompt={setPrompt} opts={opts} campaignId={editing?.id} onCount={setCount} />}
          {step === 3 && <ScriptStep />}
          {step === 4 && <AgentsStep reps={opts.reps || []} picked={picked} setPicked={setPicked} />}
          {step === 5 && <Review name={name} count={count} f={f} owners={opts.owners || []} picked={picked} editing={!!editing} />}
        </div>
        <div style={{ padding: "14px 20px", borderTop: "0.5px solid #eee", display: "flex", justifyContent: "space-between", flexShrink: 0, background: "#fafafa" }}>
          {step > 1 ? <button data-testid="cd-back-btn" onClick={() => setStep(step - 1)} className="btn" style={{ fontSize: 12 }}>Back</button> : <div />}
          {step < 5 ? (
            <button data-testid="cd-next-btn" onClick={() => setStep(step + 1)} disabled={blocked} className="btn btn-primary" style={{ fontSize: 12, opacity: blocked ? 0.5 : 1 }}>Next</button>
          ) : (
            <button data-testid="cd-builder-save" onClick={save} disabled={saving} className="btn btn-primary" style={{ fontSize: 12 }}>{saving ? "Saving…" : editing ? "Save changes" : "Launch Campaign"}</button>
          )}
        </div>
      </div>
    </div>
  );
}
